import { createIsInstanceOf } from './createIsInstanceOf.js';

export type ToSuperFn<ConstructorArgs extends any[]> =
  (...args: ConstructorArgs) => Parameters<ErrorConstructor>;

export type ToSuperType<ConstructorArgs extends any[]> =
  | ToSuperFn<ConstructorArgs>
  | string
  | Parameters<ErrorConstructor>;

export interface ErrorClass<ConstructorArgs extends any[]> {
  name: string;
  new(...args: ConstructorArgs): Error;
  /**
   * @returns True if the passed value is an instance of this class.
   * @param value - value to check.
   */
  is: (value: unknown) => value is Error;
}

/**
 * @returns A new error class with a predefined name.
 * @param name - error class name
 * @param toSuper - a function converting passed constructor arguments to a list of arguments
 * passed to the `Error` constructor. It can also be a message or a list of arguments passed
 * to the super constructor.
 */
export function errorClass<ConstructorArgs extends any[] = []>(
  name: string,
  toSuper?: ToSuperType<ConstructorArgs>,
): ErrorClass<ConstructorArgs> {
  class CustomError extends Error {
    constructor(...args: ConstructorArgs) {
      const params = typeof toSuper === 'function'
        ? toSuper(...args)
        : typeof toSuper === 'string'
          ? [toSuper] as [string]
          : toSuper || [];
      super(...params);
      this.name = name;
    }

    static is = createIsInstanceOf(CustomError);
  }

  Object.defineProperty(CustomError, 'name', { value: name });

  return CustomError;
}